/**
 * Capture Session Storage Service
 *
 * Manages sessionStorage persistence for capture mode content blocks.
 * Keeps track of captured content (images, audio, PDFs) with their
 * thumbnails, metadata, and Firestore messageIds.
 *
 * This allows:
 * - Distinguishing capture mode content from regular chat content
 * - Restoring capture mode state when reopening (thumbnails/previews)
 * - Persisting content after "Rapport opstellen" for potential re-entry
 *
 * @module features/chat/services/capture-session-service
 */

import type { ContentBlock } from "../types/messages";

// =============================================================================
// CONSTANTS
// =============================================================================

const CAPTURE_CONTENT_BLOCKS_PREFIX = "capture-content-blocks";

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get the sessionStorage key for capture content blocks
 */
function getCaptureSessionKey(conversationId: string): string {
  return `${CAPTURE_CONTENT_BLOCKS_PREFIX}-${conversationId}`;
}

/**
 * Get stored capture content blocks from sessionStorage
 *
 * @param conversationId - The conversation/project ID
 * @returns Array of content blocks, or empty array if none stored
 */
export function getCaptureContentBlocks(conversationId: string): ContentBlock[] {
  try {
    const key = getCaptureSessionKey(conversationId);
    const stored = sessionStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

/**
 * Save capture content blocks to sessionStorage
 *
 * @param conversationId - The conversation/project ID
 * @param blocks - Array of content blocks to save
 */
export function saveCaptureContentBlocks(
  conversationId: string,
  blocks: ContentBlock[]
): void {
  try {
    const key = getCaptureSessionKey(conversationId);
    if (blocks.length > 0) {
      sessionStorage.setItem(key, JSON.stringify(blocks));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear capture session from sessionStorage
 *
 * @param conversationId - The conversation/project ID
 */
export function clearCaptureSession(conversationId: string): void {
  try {
    const key = getCaptureSessionKey(conversationId);
    sessionStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
}
